import React from 'react';
import { ExclamationCircleIcon, CheckCircleIcon } from '@heroicons/react/24/outline';

interface FormInputProps extends React.InputHTMLAttributes<HTMLInputElement> {
  label: string;
  name: string;
  error?: string;
  touched?: boolean;
  success?: boolean;
  helpText?: string;
  onBlur?: (e: React.FocusEvent<HTMLInputElement>) => void;
  onChange?: (e: React.ChangeEvent<HTMLInputElement>) => void;
}

export const FormInput: React.FC<FormInputProps> = ({
  label,
  name,
  error,
  touched,
  success,
  helpText,
  className = '',
  onBlur,
  onChange,
  ...inputProps
}) => {
  const hasError = touched && error;
  const showSuccess = touched && !error && success;

  return (
    <div className="space-y-1">
      <label htmlFor={name} className="block text-sm font-semibold text-slate-700">
        {label}
        {inputProps.required && <span className="ml-1 text-rose-500">*</span>}
      </label>
      
      <div className="relative">
        <input
          id={name}
          name={name}
          className={`
            w-full rounded-lg border px-4 py-2 text-sm transition
            focus:outline-none focus:ring-2
            ${hasError
              ? 'border-rose-300 bg-rose-50 text-rose-900 focus:border-rose-500 focus:ring-rose-200'
              : showSuccess
              ? 'border-emerald-300 bg-emerald-50 text-emerald-900 focus:border-emerald-500 focus:ring-emerald-200'
              : 'border-slate-200 bg-white text-slate-900 focus:border-brand-blue focus:ring-brand-blue/20'
            }
            ${inputProps.disabled ? 'cursor-not-allowed opacity-50' : ''}
            ${className}
          `}
          onBlur={onBlur}
          onChange={onChange}
          aria-invalid={hasError ? 'true' : 'false'}
          aria-describedby={hasError ? `${name}-error` : helpText ? `${name}-help` : undefined}
          {...inputProps}
        />
        
        {/* Success/Error Icon */}
        {(hasError || showSuccess) && (
          <div className="pointer-events-none absolute inset-y-0 right-0 flex items-center pr-3">
            {hasError ? (
              <ExclamationCircleIcon className="h-5 w-5 text-rose-500" />
            ) : showSuccess ? (
              <CheckCircleIcon className="h-5 w-5 text-emerald-500" />
            ) : null}
          </div>
        )}
      </div>

      {/* Error Message */}
      {hasError && (
        <p id={`${name}-error`} className="flex items-center gap-1 text-xs text-rose-600">
          <ExclamationCircleIcon className="h-3 w-3" />
          {error}
        </p>
      )}

      {/* Help Text */}
      {!hasError && helpText && (
        <p id={`${name}-help`} className="text-xs text-slate-500">
          {helpText}
        </p>
      )}
    </div>
  );
};

interface FormTextareaProps extends React.TextareaHTMLAttributes<HTMLTextAreaElement> {
  label: string;
  name: string;
  error?: string;
  touched?: boolean;
  success?: boolean;
  helpText?: string;
  onBlur?: (e: React.FocusEvent<HTMLTextAreaElement>) => void;
  onChange?: (e: React.ChangeEvent<HTMLTextAreaElement>) => void;
}

export const FormTextarea: React.FC<FormTextareaProps> = ({
  label,
  name,
  error,
  touched,
  success,
  helpText,
  className = '',
  onBlur,
  onChange,
  ...textareaProps
}) => {
  const hasError = touched && error;
  const showSuccess = touched && !error && success;

  return (
    <div className="space-y-1">
      <label htmlFor={name} className="block text-sm font-semibold text-slate-700">
        {label}
        {textareaProps.required && <span className="ml-1 text-rose-500">*</span>}
      </label>
      
      <textarea
        id={name}
        name={name}
        className={`
          w-full rounded-lg border px-4 py-2 text-sm transition
          focus:outline-none focus:ring-2
          ${hasError
            ? 'border-rose-300 bg-rose-50 text-rose-900 focus:border-rose-500 focus:ring-rose-200'
            : showSuccess
            ? 'border-emerald-300 bg-emerald-50 text-emerald-900 focus:border-emerald-500 focus:ring-emerald-200'
            : 'border-slate-200 bg-white text-slate-900 focus:border-brand-blue focus:ring-brand-blue/20'
          }
          ${textareaProps.disabled ? 'cursor-not-allowed opacity-50' : ''}
          ${className}
        `}
        onBlur={onBlur}
        onChange={onChange}
        aria-invalid={hasError ? 'true' : 'false'}
        aria-describedby={hasError ? `${name}-error` : helpText ? `${name}-help` : undefined}
        {...textareaProps}
      />

      {/* Error Message */}
      {hasError && (
        <p id={`${name}-error`} className="flex items-center gap-1 text-xs text-rose-600">
          <ExclamationCircleIcon className="h-3 w-3" />
          {error}
        </p>
      )}

      {/* Help Text */}
      {!hasError && helpText && (
        <p id={`${name}-help`} className="text-xs text-slate-500">
          {helpText}
        </p>
      )}
    </div>
  );
};

interface FormSelectProps extends React.SelectHTMLAttributes<HTMLSelectElement> {
  label: string;
  name: string;
  error?: string;
  touched?: boolean;
  success?: boolean;
  helpText?: string;
  options: { value: string; label: string }[];
  onBlur?: (e: React.FocusEvent<HTMLSelectElement>) => void;
  onChange?: (e: React.ChangeEvent<HTMLSelectElement>) => void;
}

export const FormSelect: React.FC<FormSelectProps> = ({
  label,
  name,
  error,
  touched,
  success,
  helpText,
  options,
  className = '',
  onBlur,
  onChange,
  ...selectProps
}) => {
  const hasError = touched && error;
  const showSuccess = touched && !error && success;

  return (
    <div className="space-y-1">
      <label htmlFor={name} className="block text-sm font-semibold text-slate-700">
        {label}
        {selectProps.required && <span className="ml-1 text-rose-500">*</span>}
      </label>
      
      <select
        id={name}
        name={name}
        className={`
          w-full rounded-lg border px-4 py-2 text-sm transition
          focus:outline-none focus:ring-2
          ${hasError
            ? 'border-rose-300 bg-rose-50 text-rose-900 focus:border-rose-500 focus:ring-rose-200'
            : showSuccess
            ? 'border-emerald-300 bg-emerald-50 text-emerald-900 focus:border-emerald-500 focus:ring-emerald-200'
            : 'border-slate-200 bg-white text-slate-900 focus:border-brand-blue focus:ring-brand-blue/20'
          }
          ${selectProps.disabled ? 'cursor-not-allowed opacity-50' : ''}
          ${className}
        `}
        onBlur={onBlur}
        onChange={onChange}
        aria-invalid={hasError ? 'true' : 'false'}
        aria-describedby={hasError ? `${name}-error` : helpText ? `${name}-help` : undefined}
        {...selectProps}
      >
        {options.map((option) => (
          <option key={option.value} value={option.value}>
            {option.label}
          </option>
        ))}
      </select>

      {/* Error Message */}
      {hasError && (
        <p id={`${name}-error`} className="flex items-center gap-1 text-xs text-rose-600">
          <ExclamationCircleIcon className="h-3 w-3" />
          {error}
        </p>
      )}

      {/* Help Text */}
      {!hasError && helpText && (
        <p id={`${name}-help`} className="text-xs text-slate-500">
          {helpText}
        </p>
      )}
    </div>
  );
};
